import React, { useContext, useEffect, useRef, useState } from 'react';
import { classNames } from '../../utils/style';
import { useScreenSize } from '../../utils/utils';
import { CarouselContext, CarouselContextType } from './CarouselContext';
import {
  handleClickDot,
  handleClickNext,
  handleClickPrev,
  useAutoScroll,
  useDisableChildrenLink,
  useScroll,
} from './logic';
import { CarouselContentProps, CarouselProps } from './type';

export const getTargetScreen = (carouselContainerRef, isItemsCentered) => {
  if (isItemsCentered) {
    return carouselContainerRef?.current?.offsetWidth
      ? carouselContainerRef?.current?.offsetWidth / 2
      : 0;
  }

  return 0;
};

const CarouselContent = ({
  className,
  classNameWrapper,
  slides,
  pagination,
  gap = 20,
  classNameContainer,
  classNameCarousel,
  classNameSlide,
  classNameSelectedSlide,
  swippable,
  isItemsCentered = false,
  isSurrounded = false,
}: CarouselContentProps) => {
  const screensize = useScreenSize();
  const [translateX, setTranslateX] = useState(0);

  const { elementSelected, paginationPosition } =
    useContext<CarouselContextType>(CarouselContext);

  const elementRefs = useRef<any[]>([]);
  const carouselRef = useRef<any>(null);
  const carouselContainerRef = useRef<any>(null);

  useAutoScroll(elementRefs);

  const {
    forceRerender,
    handleMouseUp,
    handleMouseMove,
    handleMouseDown,
    isDragging,
    isMove,
    overideTranslate,
  } = useScroll({
    elementRefs,
    translateX,
    carouselContainerRef,
    isItemsCentered,
    carouselRef,
  });

  useDisableChildrenLink(carouselRef, isMove, isDragging);

  useEffect(() => {
    const targetScreen = getTargetScreen(carouselContainerRef, isItemsCentered);
    const selectedElemWidth = elementRefs.current[elementSelected]?.offsetWidth;

    const movePixel = isItemsCentered
      ? targetScreen - selectedElemWidth / 2
      : 0;

    setTranslateX(movePixel);
  }, [isItemsCentered, forceRerender, screensize, elementSelected]);

  useEffect(() => {
    if (elementSelected < 0 || elementSelected >= elementRefs.current.length)
      return; // Exit if index is out of bounds
    const targetScreen = getTargetScreen(carouselContainerRef, isItemsCentered);

    const itemWidth = elementRefs.current?.[elementSelected].offsetWidth; // Calculate width of the current item
    const itemWidthAndGap = itemWidth + gap; // Calculate width of the current item

    // Calculate translation for the selected index
    let translateX = 0;
    if (isItemsCentered) {
      translateX =
        targetScreen - itemWidth / 2 - elementSelected * itemWidthAndGap;
    } else {
      translateX = targetScreen - elementSelected * itemWidthAndGap;
    }

    setTranslateX(translateX);
  }, [elementSelected, isItemsCentered, forceRerender, screensize, gap]);

  const isNotSwipper = !swippable || slides.length <= 1;

  const getTranslationStyles = () => {
    if (swippable)
      return {
        transform: overideTranslate
          ? `translate3d(${overideTranslate}px, 0, 0)`
          : `translateX(${translateX}px)`,
        transition: overideTranslate ? 'none' : 'transform 0.5s ease',
      };
    else return;
  };

  return (
    <div
      className={classNames(
        'flex',
        paginationPosition === 'top' ? 'flex-col-reverse' : 'flex-col',
        'overflow-x-hidden',
        'relative w-full',
        '-my-[30px] py-[30px]',
        classNameWrapper,
      )}
    >
      <div className={classNames(classNameContainer)}>
        <div
          ref={carouselContainerRef}
          draggable={false}
          className={classNames('CarouselContainer', className)}
          onMouseDown={isNotSwipper ? () => {} : handleMouseDown}
          onMouseUp={isNotSwipper ? () => {} : handleMouseUp}
          onMouseMove={isNotSwipper ? () => {} : handleMouseMove}
          onTouchStart={isNotSwipper ? () => {} : handleMouseDown}
          onTouchEnd={isNotSwipper ? () => {} : handleMouseUp}
          onTouchMove={isNotSwipper ? () => {} : handleMouseMove}
        >
          <div
            ref={carouselRef}
            className={classNames(
              'Carousel',
              classNameCarousel,
              'relative flex flex-row',
              isItemsCentered ? 'justify-center' : 'justify-start',
              'w-max',
            )}
            style={getTranslationStyles()}
          >
            {slides.map((slide, key) => {
              return (
                <div
                  draggable={false}
                  ref={(el) => (elementRefs.current[key] = el)}
                  key={key}
                  className={classNames(
                    classNameSlide,
                    `Slide flex items-center justify-center`,
                    key === elementSelected ? classNameSelectedSlide : '',
                    isSurrounded
                      ? key !== elementSelected
                        ? 'opacity-60'
                        : ''
                      : '',
                    isSurrounded
                      ? key === elementSelected
                        ? 'border-2 border-solid border-primary-1'
                        : 'border-2 border-transparent'
                      : '',
                  )}
                >
                  {slide}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {!isNotSwipper && pagination}
    </div>
  );
};

/**
 * @param isLoop: boolean - True if the carousel has pagination loop
 * @param isAutoplay: boolean - True if the carousel is autoplay
 * @param isItemsCentered: boolean - True if each item is centered to the screen -
 *        need to be added also on the pagination component linked exept for the dots.
 * @param isSurrounded: boolean - True if item must be surrounded by a border
 */
export const Carousel = ({
  className,
  classNameWrapper,
  classNameContainer,
  classNameSlide,
  classNameCarousel,
  classNameSelectedSlide,
  classNamePagination,
  isDark,
  gap = 20,
  isLoop = false,
  isAutoplay = false,
  initialSelected = 0,
  paginationPosition = 'bottom',
  children,
  pagination,
  swippable = true,
  isItemsCentered = false,
  isSurrounded = false,
  onClickPrev,
  onClickNext,
  onClickDot,
}: CarouselProps) => {
  const [elementSelected, setElementSelected] =
    useState<number>(initialSelected);
  const screensize = useScreenSize();

  return (
    <CarouselContext.Provider
      value={{
        nbElement: children?.length,
        isDark,
        isLoop,
        paginationPosition,
        classNamePagination,
        elementSelected,
        isAutoplay,
        screensize,
        setElementSelected,
        onClickDot: (indexElement: number) => {
          handleClickDot({
            setElementSelected,
            elementSelected: indexElement,
            onClickDot,
          });
        },
        onClickNext: () => {
          handleClickNext({
            setElementSelected,
            nbElement: children?.length,
            isLoop,
            onClickNext,
          });
        },
        onClickPrev: () => {
          handleClickPrev({
            setElementSelected,
            nbElement: children?.length,
            isLoop,
            onClickPrev,
          });
        },
      }}
    >
      <CarouselContent
        slides={children}
        gap={gap}
        pagination={pagination}
        className={className}
        classNameWrapper={classNameWrapper}
        classNameContainer={classNameContainer}
        classNameCarousel={classNameCarousel}
        classNameSlide={classNameSlide}
        classNameSelectedSlide={classNameSelectedSlide}
        swippable={swippable}
        isItemsCentered={isItemsCentered}
        isSurrounded={isSurrounded}
      />
    </CarouselContext.Provider>
  );
};
